import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { TD, TD_MUTED, TD_NUM, TH, TH_NUM, THEAD } from '@/pages/cvm/shared';
import { fetchCvmDirectors } from '@/services/cvm';
import type { CvmRosterRow } from '@/services/types';

/**
 * The bench: every director, presidente and CRSFN conselheiro the judgments name.
 *
 * Counts only -- judgments reported, positions recorded by kind, first and last
 * year seen. Ordered by name, never by a count, so the table does not rank
 * people by how often the lot fell on them. Rates are refused on the profile
 * page with the funnel that would be needed to compute one.
 */
export function CvmDirectorsPage() {
  const { t, i18n } = useTranslation(['cvm', 'common']);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['cvm', 'directors'],
    queryFn: fetchCvmDirectors,
  });
  const number = new Intl.NumberFormat(i18n.language);

  if (isPending) return <SkeletonLoader rows={8} />;
  if (error) return <ErrorMessage message={error.message} onRetry={() => void refetch()} />;

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('cvm:directors.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('cvm:directors.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="cvm:directors.explainer" />
      <AsOfStrip asOf={data.as_of} />

      <section className="rounded-lg border bg-card p-4" data-roster>
        <h2 className="mb-2 text-sm font-semibold text-foreground">
          {t('cvm:directors.roster.title')}
        </h2>
        <Answer answer={data.roster} help={t('cvm:directors.roster.help')}>
          {(rows: CvmRosterRow[]) => (
            <div className="max-h-[65vh] overflow-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className={THEAD}>
                  <tr>
                    <th className={TH}>{t('cvm:directors.roster.columns.name')}</th>
                    <th className={TH}>{t('cvm:directors.roster.columns.body')}</th>
                    <th className={TH_NUM}>{t('cvm:directors.roster.columns.relator')}</th>
                    <th className={TH_NUM}>{t('cvm:directors.roster.columns.rows')}</th>
                    <th className={TH_NUM}>{t('cvm:directors.roster.columns.followsRelator')}</th>
                    <th className={TH_NUM}>{t('cvm:directors.roster.columns.followsDissent')}</th>
                    <th className={TH_NUM}>{t('cvm:directors.roster.columns.dissents')}</th>
                    <th className={TH_NUM}>{t('cvm:directors.roster.columns.recused')}</th>
                    <th className={TH}>{t('cvm:directors.roster.columns.years')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.slug} className="border-t hover:bg-muted/30">
                      <td className={TD}>
                        <Link
                          to={`/cvm/r/${encodeURIComponent(row.slug)}`}
                          className="text-primary underline-offset-2 hover:underline"
                        >
                          {row.name}
                        </Link>
                        {row.role ? (
                          <div className="text-xs text-muted-foreground">{row.role}</div>
                        ) : null}
                      </td>
                      <td className={TD_MUTED}>
                        {t(`cvm:bodies.${row.body}`, {
                          defaultValue: row.body,
                        })}
                      </td>
                      <td className={TD_NUM}>{number.format(row.relator_decisions)}</td>
                      <td className={TD_NUM}>{number.format(row.vote_rows)}</td>
                      <td className={TD_NUM}>{number.format(row.follows_relator)}</td>
                      <td className={TD_NUM}>{number.format(row.follows_dissent)}</td>
                      <td className={TD_NUM}>{number.format(row.dissents)}</td>
                      <td className={TD_NUM}>{number.format(row.recused)}</td>
                      <td className={TD_MUTED}>
                        {row.first_year && row.last_year
                          ? row.first_year === row.last_year
                            ? String(row.first_year)
                            : `${row.first_year}–${row.last_year}`
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Answer>
      </section>

      <FieldGuide i18nKey="cvm:directors.guide" />
    </div>
  );
}
